import React, { useLayoutEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  CardHeader,
  Divider,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import StatusMark from "./StatusMark";
import { styles } from "../styles/publicStyles";
import VendorLogo from "./VendorLogo";

const PREVIEW = 4;
// Rows shown when the card is not filling a column (phones, tests). When it
// fills a column on wide screens it reserves one row and shows however many
// whole rows the left column leaves room for.
const CONTROLS_MIN_ROWS = 3;
const CONTROLS_MAX_ROWS = 30;
// Height of one control row (title + one-line description), used to reserve
// the minimum space before the real rows have been measured.
const CONTROL_ROW_PX = 70;
// Controls listed on the Overview's full-width card.
const CONTROLS_SHOWN = 4;

const ControlRow = ({ control }) => {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      sx={{
        px: 2.5,
        py: 1.5,
        borderTop: `1px solid ${theme.customColors.border.main}`,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          noWrap
          sx={{ fontWeight: 500, fontSize: 14, color: theme.customColors.text.strong }}
        >
          {control.title}
        </Typography>
        {control.description && (
          <Typography
            variant="body2"
            noWrap
            title={control.description}
            sx={{ mt: 0.25, color: theme.customColors.text.secondary }}
          >
            {control.description}
          </Typography>
        )}
      </Box>
      <StatusMark status={control.status} />
    </Stack>
  );
};

// No "View all" in the header: a card links to its tab only when it is
// holding items back, with "View N more" under the list (MoreLink).
const SectionCard = ({ title, subheader, sx, children }) => (
  <Card sx={{ ...styles.mainCard, ...sx }}>
    <CardHeader sx={styles.cardHeader} title={title} subheader={subheader} />
    {children}
  </Card>
);

const MoreLink = ({ count, onClick }) => {
  const theme = useTheme();
  if (count <= 0) return null;
  return (
    <Box
      sx={{
        px: 2.5,
        py: 1.5,
        borderTop: `1px solid ${theme.customColors.border.main}`,
      }}
    >
      <Link component="button" variant="body2" onClick={onClick}>
        View {count} more
      </Link>
    </Box>
  );
};

// How many whole rows fit in `box`. Rows past that are hidden, never cut in
// half. With no layout (narrow screens before paint, tests) it keeps `fallback`.
const useRowsThatFit = (boxRef, total, fallback) => {
  const [fit, setFit] = useState(Math.min(total, fallback));
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return undefined;
    const measure = () => {
      const height = box.clientHeight;
      if (!height) {
        setFit(Math.min(total, fallback));
        return;
      }
      const rows = [...box.querySelectorAll("[data-control-row]")];
      const count = rows.filter(
        (row) => row.offsetTop + row.offsetHeight <= height + 1
      ).length;
      setFit(Math.max(1, count));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    return () => observer.disconnect();
  }, [boxRef, total, fallback]);
  return fit;
};

// Same row style as every other list on the Overview: plain rows with thin
// dividers, no boxes inside the card.
// With `fill`, the card stretches to the height of its grid row (set by the
// column beside it) and shows as many controls as fit, so there is no gap.
// The list itself is absolutely positioned, so it never makes the row taller.
export const ControlsPreview = ({ controls, onViewAll, fill = false }) => {
  const theme = useTheme();
  const listRef = useRef(null);
  const candidates = (controls || []).slice(0, CONTROLS_MAX_ROWS);
  const fit = useRowsThatFit(listRef, candidates.length, CONTROLS_MIN_ROWS);
  if (candidates.length === 0) return null;
  // Not filling a column (the Overview's full-width card): a plain list of
  // the first few, no measuring and no gap under it.
  if (!fill) {
    const shown = candidates.slice(0, CONTROLS_SHOWN);
    return (
      <SectionCard title="Controls" subheader="Checked against our live systems.">
        <Box>
          {shown.map((control) => (
            <ControlRow key={control.id} control={control} />
          ))}
        </Box>
        <MoreLink count={(controls || []).length - shown.length} onClick={onViewAll} />
      </SectionCard>
    );
  }
  const extra = (controls || []).length - fit;

  return (
    <SectionCard
      title="Controls"
      subheader="Checked against our live systems."
      sx={fill ? { flex: 1, display: "flex", flexDirection: "column" } : {}}
    >
      <Box
        sx={{
          position: "relative",
          flex: 1,
          // Space reserved for rows. Filling a column on wide screens: one
          // row, so the column beside sets the height. Otherwise: three.
          minHeight: fill
            ? {
                xs:
                  Math.min(candidates.length, CONTROLS_MIN_ROWS) *
                  CONTROL_ROW_PX,
                md: CONTROL_ROW_PX,
              }
            : Math.min(candidates.length, CONTROLS_MIN_ROWS) * CONTROL_ROW_PX,
        }}
      >
        <Box
          ref={listRef}
          sx={{ position: "absolute", inset: 0, overflow: "hidden" }}
        >
          {candidates.map((control, index) => (
            <Stack
              key={control.id}
              data-control-row
              direction="row"
              spacing={2}
              alignItems="center"
              aria-hidden={index >= fit ? true : undefined}
              sx={{
                px: 2.5,
                py: 1.5,
                borderBottom: `1px solid ${theme.customColors.border.main}`,
                // Laid out (so it can be measured) but not shown.
                visibility: index < fit ? "visible" : "hidden",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: 14,
                    color: theme.customColors.text.strong,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {control.title}
                </Typography>
                {control.description && (
                  <Typography
                    variant="body2"
                    title={control.description}
                    sx={{
                      mt: 0.25,
                      color: theme.customColors.text.secondary,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {control.description}
                  </Typography>
                )}
              </Box>
              {(control.status === "pass" || control.status === "fail") && (
                <Box sx={{ flexShrink: 0 }}>
                  <StatusMark status={control.status} />
                </Box>
              )}
            </Stack>
          ))}
        </Box>
      </Box>
      <MoreLink count={extra} onClick={onViewAll} />
    </SectionCard>
  );
};

export const SubprocessorsPreview = ({ subprocessors, onViewAll }) => {
  const theme = useTheme();
  const shown = (subprocessors || []).slice(0, PREVIEW);
  if (shown.length === 0) return null;
  const extra = subprocessors.length - shown.length;

  return (
    <SectionCard title="Subprocessors">
      <Stack
        divider={
          <Divider sx={{ borderColor: theme.customColors.border.main }} />
        }
      >
        {shown.map((row) => (
          <Stack
            key={row.id}
            direction="row"
            spacing={2}
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: 2.5, py: 1.25 }}
          >
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="center"
              sx={{ minWidth: 0 }}
            >
              <VendorLogo name={row.name} logoPath={row.logoPath} size={26} />
              <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                {row.name}
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              sx={{
                color: theme.customColors.text.secondary,
                textAlign: "right",
              }}
            >
              {row.purpose}
            </Typography>
          </Stack>
        ))}
      </Stack>
      <MoreLink count={extra} onClick={onViewAll} />
    </SectionCard>
  );
};
